(() => {
  if (window.__eisenhowerPersonalizationLoaded) return;
  window.__eisenhowerPersonalizationLoaded = true;

  const YOUTH_SIZES = ['XS', 'S', 'M', 'L', 'XL'];
  const ADULT_SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

  function normalizeEisenhowerSize(event) {
    const garmentInput = event.target;
    if (
      !(garmentInput instanceof HTMLInputElement) ||
      garmentInput.type !== 'radio' ||
      !garmentInput.name.startsWith('Garment-')
    ) {
      return;
    }

    const variantSelects = garmentInput.closest('variant-selects');
    if (!variantSelects) return;

    const allowedSizes = garmentInput.value.startsWith('Youth')
      ? YOUTH_SIZES
      : garmentInput.value.startsWith('Adult')
        ? ADULT_SIZES
        : null;

    if (!allowedSizes) return;

    const selectedSize = variantSelects.querySelector(
      'input[type="radio"][name^="Size-"]:checked'
    );

    if (selectedSize && allowedSizes.includes(selectedSize.value)) return;

    const sizeInputs = Array.from(
      variantSelects.querySelectorAll('input[type="radio"][name^="Size-"]')
    );

    const fallback = allowedSizes
      .map((size) => sizeInputs.find((input) => input.value === size && !input.disabled))
      .find(Boolean);

    if (fallback) fallback.checked = true;
  }

  document.addEventListener('change', normalizeEisenhowerSize, true);

  const SELECTORS = {
    panel: '[data-eisenhower-personalization]',
    toggle: '[data-eisenhower-personalization-toggle]',
    fields: '[data-eisenhower-personalization-fields]',
    name: '[data-eisenhower-back-name]',
  };

  function getPanelFromForm(form) {
    return form?.querySelector(SELECTORS.panel) || null;
  }

  function getQuantityInputs(form) {
    if (!form) return [];
    return Array.from(document.querySelectorAll('input[name="quantity"]')).filter((input) => input.form === form);
  }

  function lockQuantity(form, locked) {
    getQuantityInputs(form).forEach((input) => {
      const quantityInput = input.closest('quantity-input');
      const buttons = quantityInput ? quantityInput.querySelectorAll('button') : [];

      if (locked) {
        if (!Object.prototype.hasOwnProperty.call(input.dataset, 'eisenhowerPreviousQuantity')) {
          input.dataset.eisenhowerPreviousQuantity = input.value || '1';
          input.dataset.eisenhowerWasReadOnly = input.readOnly ? 'true' : 'false';
        }
        input.value = '1';
        input.readOnly = true;

        buttons.forEach((button) => {
          if (!Object.prototype.hasOwnProperty.call(button.dataset, 'eisenhowerWasDisabled')) {
            button.dataset.eisenhowerWasDisabled = button.disabled ? 'true' : 'false';
          }
          button.disabled = true;
        });
      } else {
        if (Object.prototype.hasOwnProperty.call(input.dataset, 'eisenhowerPreviousQuantity')) {
          input.value = input.dataset.eisenhowerPreviousQuantity || '1';
          input.readOnly = input.dataset.eisenhowerWasReadOnly === 'true';
          delete input.dataset.eisenhowerPreviousQuantity;
          delete input.dataset.eisenhowerWasReadOnly;
        }

        buttons.forEach((button) => {
          if (Object.prototype.hasOwnProperty.call(button.dataset, 'eisenhowerWasDisabled')) {
            button.disabled = button.dataset.eisenhowerWasDisabled === 'true';
            delete button.dataset.eisenhowerWasDisabled;
          }
        });
      }
    });
  }

  function syncPanel(panel) {
    if (!panel) return;
    const form = panel.closest('form[data-type="add-to-cart-form"]');
    const productForm = form?.closest('product-form');
    const toggle = panel.querySelector(SELECTORS.toggle);
    const fields = panel.querySelector(SELECTORS.fields);
    const nameInput = panel.querySelector(SELECTORS.name);
    const active = Boolean(toggle?.checked);

    if (fields) fields.hidden = !active;
    if (nameInput) {
      nameInput.required = active;
      if (!active) nameInput.setCustomValidity('');
    }

    productForm?.classList.toggle('eisenhower-personalization-active', active);
    lockQuantity(form, active);
  }

  function setLoading(productForm, loading) {
    const submitButton = productForm?.submitButton || productForm?.querySelector('[type="submit"]');
    const spinner = productForm?.querySelector('.loading__spinner');
    if (!submitButton) return;

    if (loading) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
      spinner?.classList.remove('hidden');
    } else {
      submitButton.classList.remove('loading');
      submitButton.removeAttribute('aria-disabled');
      spinner?.classList.add('hidden');
    }
  }

  function showError(productForm, message) {
    if (typeof productForm?.handleErrorMessage === 'function') {
      productForm.handleErrorMessage(message || false);
      return;
    }

    const wrapper = productForm?.querySelector('.product-form__error-message-wrapper');
    const target = wrapper?.querySelector('.product-form__error-message');
    if (!wrapper || !target) return;
    wrapper.toggleAttribute('hidden', !message);
    if (message) target.textContent = message;
  }

  async function parseResponse(response) {
    const data = await response.json();
    if (!response.ok || data?.status) {
      throw new Error(data?.description || data?.message || 'Unable to add this item to the cart.');
    }
    return data;
  }

  async function addPersonalizationFee(panel, backName, personalizationId) {
    const addonVariantId = Number(panel.dataset.addonVariantId);
    if (!addonVariantId) throw new Error('Personalization is temporarily unavailable.');

    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [
          {
            id: addonVariantId,
            quantity: 1,
            properties: {
              'Back Name': backName,
              _For: panel.dataset.productTitle || 'Eisenhower Spirit Wear',
              '_Personalization ID': personalizationId,
              '_Personalization Role': 'addon',
            },
          },
        ],
      }),
    });

    const data = await parseResponse(response);
    const line = Array.isArray(data?.items) ? data.items[0] : data;
    return line?.key || null;
  }

  async function removePersonalizationFee(lineKey) {
    if (!lineKey) return;
    const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });
    if (!response.ok) throw new Error('Unable to roll back personalization fee.');
  }

  async function addGarment(form, productForm, backName, personalizationId) {
    const formData = new FormData(form);
    formData.set('quantity', '1');
    formData.set('properties[Back Name]', backName);
    formData.set('properties[_Personalization ID]', personalizationId);
    formData.set('properties[_Personalization Role]', 'garment');

    const cart = productForm?.cart || document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement?.(document.activeElement);
    }

    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    const data = await parseResponse(response);
    return { data, formData, cart };
  }

  function renderCart(productForm, cart, data, formData) {
    if (!cart) {
      window.location = window.routes?.cart_url || '/cart';
      return;
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          cartData: data,
        });
      } catch (error) {
        console.error(error);
      }
    }

    const quickAddModal = productForm?.closest('quick-add-modal');
    if (quickAddModal) {
      document.body.addEventListener(
        'modalClosed',
        () => {
          setTimeout(() => cart.renderContents(data));
        },
        { once: true }
      );
      quickAddModal.hide(true);
    } else {
      cart.renderContents(data);
    }
  }

  document.addEventListener('change', (event) => {
    const toggle = event.target.closest?.(SELECTORS.toggle);
    if (!toggle) return;
    syncPanel(toggle.closest(SELECTORS.panel));
  });

  document.addEventListener('input', (event) => {
    const nameInput = event.target.closest?.(SELECTORS.name);
    if (!nameInput) return;
    nameInput.setCustomValidity('');
    const productForm = nameInput.closest('product-form');
    showError(productForm, false);
  });

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[data-type="add-to-cart-form"]')) return;

      const panel = getPanelFromForm(form);
      const toggle = panel?.querySelector(SELECTORS.toggle);
      if (!panel || !toggle?.checked) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const productForm = form.closest('product-form');
      const submitButton = productForm?.submitButton || productForm?.querySelector('[type="submit"]');
      if (submitButton?.getAttribute('aria-disabled') === 'true') return;

      const nameInput = panel.querySelector(SELECTORS.name);
      const backName = nameInput?.value.trim() || '';
      if (!backName) {
        nameInput?.setCustomValidity('Please enter the name you want printed on the back.');
        nameInput?.reportValidity();
        return;
      }

      if (backName.length > 15) {
        nameInput?.setCustomValidity('Please keep the name to 15 characters or fewer.');
        nameInput?.reportValidity();
        return;
      }

      showError(productForm, false);
      setLoading(productForm, true);

      const personalizationId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `eis-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      let feeLineKey = null;

      try {
        feeLineKey = await addPersonalizationFee(panel, backName, personalizationId);
        const { data, formData, cart } = await addGarment(form, productForm, backName, personalizationId);
        productForm.error = false;
        if (cart?.classList.contains('is-empty')) cart.classList.remove('is-empty');
        renderCart(productForm, cart, data, formData);
      } catch (error) {
        if (feeLineKey) {
          try {
            await removePersonalizationFee(feeLineKey);
          } catch (rollbackError) {
            console.error(rollbackError);
          }
        }
        console.error(error);
        productForm.error = true;
        showError(productForm, error?.message || 'Unable to add this item to the cart.');
      } finally {
        setLoading(productForm, false);
      }
    },
    true
  );

  document.querySelectorAll(SELECTORS.panel).forEach(syncPanel);
})();
